import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { DataTableApiId } from '@/model/DataTable.model';
import { ID } from '@/model/Common.model';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { isApiId } from '@/utils/ApiId.utils';
import { Study } from '@/model/Study.model';
import { DataTableService } from '@/components/UI/DataTableV2/service/DataTableService';
import { _notNil } from '@/littledash';
import * as Auth from '@/support/auth';
import { DataTableApiService } from '@/components/UI/DataTableV2/service/DataTableApiService';
import http from '@/support/http';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';
import { useSelector } from 'react-redux';
import {
  DataTableRowHeaderId,
  DataTableService as IDataTableService,
} from '@/components/UI/DataTableV2/DataTable.model';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import axios from 'axios';

const getUserFriendlyErrorMessage = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
      return 'Network error: Unable to connect to server. Please check your connection and try again.';
    }
    if (err.response?.status === 401) {
      return 'Authentication failed. Please log in again.';
    }
    if (err.response?.status === 403) {
      return 'Permission denied. You may not have access to this DataTable.';
    }
    if (err.response?.status === 404) {
      return 'The DataTable could not be found. It may have been deleted.';
    }
    if (err.response?.status && err.response.status >= 500) {
      return 'Server error occurred. Please try again or contact support.';
    }
  }

  return 'An unexpected error occurred loading the DataTable. Please refresh or contact support.';
};

type InitializedDataTableContext = {
  type: 'initialized';
  studyApiId: string;
  dataTableApiId: string;
  dataTableService: IDataTableService;
};

type UninitializedDataTableContext = {
  type: 'uninitialized';
};

type ErrorDataTableContext = {
  type: 'error';
  message: string;
};

type DataTableContext = InitializedDataTableContext | UninitializedDataTableContext | ErrorDataTableContext;

const context = createContext<DataTableContext>({ type: 'uninitialized' });

export type DataTableProviderProps = { studyId: ID; dataTableApiId: DataTableApiId };

export const useDataTable = (): InitializedDataTableContext => {
  const dataTableContext = useContext(context);
  if (dataTableContext.type === 'initialized') {
    return dataTableContext;
  }
  throw new InVivoError('DataTable context not initialized', { slug: 'data-table-not-initialized' });
};

const dataTableObservationsEnabledSelector = createSelector(
  [featuresSelector],
  (features) => features.datatables_observation === true
);
export const DataTableProvider: FC<PropsWithChildren<DataTableProviderProps>> = ({
  studyId,
  dataTableApiId,
  children,
}) => {
  const [contextValue, setContextValue] = useState<DataTableContext>({ type: 'uninitialized' });
  const dataTableObservationsEnabled = useSelector(dataTableObservationsEnabledSelector);
  const { response: studyLoadResponse } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.show.p',
    path: { id: studyId },
    options: { invokeOnInit: true, onError: { slug: 'datatable-study-load' } },
  });

  useEffect(() => {
    if (studyLoadResponse?.type === 'success' && isApiId('dtl', dataTableApiId)) {
      const study = (studyLoadResponse.body as { data: Study }).data;
      const dataTableService = new DataTableService({
        readonly: !(_notNil(study) && Auth.isWriteUserForStudy(study)),
        apiService: new DataTableApiService({ dataTableId: dataTableApiId, studyId: study.api_id, axios: http }),
        studyApiId: study.api_id,
        rowHeaders: [
          { id: DataTableRowHeaderId.animalName, title: 'Name', displayed: true },
          {
            id: DataTableRowHeaderId.animalStudyGroup,
            title: 'Study Group',
            displayed: true,
          },
          { id: DataTableRowHeaderId.animalCage, title: 'Cage', displayed: false },
          {
            id: DataTableRowHeaderId.animalTail,
            title: 'Tail',
            displayed: true,
          },
          {
            id: DataTableRowHeaderId.animalEar,
            title: 'Ear',
            displayed: false,
          },
          {
            id: DataTableRowHeaderId.animalTag,
            title: 'Tag',
            displayed: false,
          },
          {
            id: DataTableRowHeaderId.animalDonor,
            title: 'Donor',
            displayed: false,
          },
        ],
        pinnedColumns: [],
        dimensions: {
          cellWidth: 150,
          cellHeight: 27,
          columnHeight: 40,
        },
        featureFlags: {
          observation: dataTableObservationsEnabled,
        },
      });
      dataTableService
        .initialise()
        .then((dtlService) =>
          setContextValue({
            type: 'initialized',
            studyApiId: study.api_id,
            dataTableApiId,
            dataTableService: dtlService,
          })
        )
        .catch((err) => {
          const error = new InVivoError('Failed to initialize DataTableService', {
            cause: err,
            slug: 'data-table-service-init',
          });
          ExceptionHandler.captureException(error);
          setContextValue({
            type: 'error',
            message: getUserFriendlyErrorMessage(err),
          });
        });
      return () => {
        dataTableService.destroy();
      };
    }
  }, [studyLoadResponse, dataTableApiId, dataTableObservationsEnabled]);

  if (contextValue.type === 'initialized') {
    return <context.Provider value={contextValue}>{children}</context.Provider>;
  }

  if (contextValue.type === 'error') {
    return (
      <div className="flex flex-column items-center justify-center pa4" style={{ minHeight: '400px' }}>
        <div className="ba b--red br2 pa3 bg-washed-red red" style={{ maxWidth: '600px' }}>
          <h3 className="mt0 mb2">Failed to Load DataTable</h3>
          <p className="ma0">{contextValue.message}</p>
        </div>
      </div>
    );
  }

  return null;
};
